import Canvas from 'editor/components/Canvas';
import Cursor, { CursorTypes } from 'editor/components/Cursor';
import Events from 'editor/components/Events';
import DrawTempRectInteraction from 'editor/components/interactions/DrawTempRectInteraction';
import {
  MapboxEventArgs,
  MapboxToolEvents,
} from 'editor/components/map/MapboxEvents/mapboxEventHandlers';
import MapSheetEvents from 'editor/components/map/MapboxEvents/MapSheetEvents';
import MapboxTool, { MapboxTools } from 'editor/components/map/MapboxTool';
import Markup from 'editor/components/Markup';
import MarkupKit from 'editor/components/MarkupKit';
import RFIPost from 'editor/components/RFIPost';
import Transform from 'editor/components/Transform';
import { Point, Rect } from 'editor/geometry';
import { getSheetLayerHit } from 'editor/utils/map';
import { Markup as MarkupRecord } from 'records/Markup';
import { v4 as uuidv4 } from 'uuid';

const startEvent = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mousedown' | 'touchstart']>
) => {
  const { world, evt, map } = args;

  const rfiPostEntity = world.locate(RFIPost);

  if (rfiPostEntity) {
    const rfiPostComp = rfiPostEntity.get(RFIPost);
    const sheetLayerEntity = world.getTagged(
      `sheetLayer-${rfiPostComp.pageid}`
    );

    if (!sheetLayerEntity) return;

    const { hit } = getSheetLayerHit(
      sheetLayerEntity,
      evt.lngLat.lng,
      evt.lngLat.lat
    );

    if (hit) {
      map.map?.dragPan.disable();
      const { x, y } = hit;

      const markupsOverlay = world.getTagged(
        `markupsLayer-${rfiPostComp.pageid}`
      );

      if (markupsOverlay) {
        const markupsOverlayCanvas = markupsOverlay.get(Canvas);
        const markupsOverlayTransform = markupsOverlay.get(Transform);

        sheetLayerEntity
          .add(markupsOverlayCanvas)
          .add(markupsOverlayTransform)
          .add(
            new DrawTempRectInteraction({
              originX: x,
              originY: y,
              currentX: x,
              currentY: y,
              stroke: `#0000FF`,
              strokeWidth: 3,
            })
          );
      }
    }
  }
};

const moveEvent = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mousemove' | 'touchmove']>
) => {
  const { world, evt } = args;

  world.grabAll(Cursor).forEach(({ component }) => {
    component.disabled = true;
  });

  const rfiPostEntity = world.locate(RFIPost);
  if (rfiPostEntity) {
    const rfiPostComp = rfiPostEntity.get(RFIPost);

    const sheetLayerEntity = world.getTagged(
      `sheetLayer-${rfiPostComp.pageid}`
    );

    if (!sheetLayerEntity) return;

    const { hit } = getSheetLayerHit(
      sheetLayerEntity,
      evt.lngLat.lng,
      evt.lngLat.lat
    );

    if (hit) {
      world.grabAll(Cursor).forEach(({ component }) => {
        component.disabled = false;
        component.cursor = CursorTypes.Crosshair;
      });

      const { x, y } = hit;
      const dtri = world.locate(DrawTempRectInteraction);

      if (dtri) {
        const interaction = dtri.get(DrawTempRectInteraction);

        interaction.currentX = x;
        interaction.currentY = y;
      }
    }
  }
};

const endEvent = (
  args: MapboxEventArgs<mapboxgl.MapEventType['mouseup' | 'touchend']>
) => {
  const { world, map, evt } = args;

  map.map?.dragPan.enable();

  world.grabAll(Cursor).forEach(({ component }) => {
    component.cursor = CursorTypes.Grab;
  });

  const rfiPostEntity = world.locate(RFIPost);
  if (!rfiPostEntity) return;

  const markupKit = world.getTagged('markupKit');
  if (!markupKit) return;

  const rfiPostComp = rfiPostEntity.get(RFIPost);

  const sheetLayerEntity = world.getTagged(`sheetLayer-${rfiPostComp.pageid}`);

  if (!sheetLayerEntity) return;

  const { hit } = getSheetLayerHit(
    sheetLayerEntity,
    evt.lngLat.lng,
    evt.lngLat.lat
  );

  // cursor is now outside the bounds of the sheet.
  if (!hit) {
    world.grabAll(MapboxTool).forEach(({ component: tool }) => {
      console.log('no layer hits');
      tool.tool = MapboxTools.Pointer;
    });
  } else {
    const { x, y } = hit;

    world.grabAll(DrawTempRectInteraction).forEach(({ entity, component }) => {
      if (x !== component.originX && y !== component.originY) {
        const { MKMarkup, MKSpan, MKBorderStyle } = markupKit.get(MarkupKit);
        const { originX, originY, currentX, currentY } = component;
        const p1 = new Point(originX, originY);
        const p2 = new Point(currentX, currentY);
        const box = Rect.fromPoints(p1, p2);

        const rfiMarkupText =
          rfiPostComp.rfi.versionnumber === 0
            ? `RFI #${rfiPostComp.rfi.number}`
            : `RFI #${rfiPostComp.rfi.number}.${
                rfiPostComp.rfi.versionnumber
              } ${rfiPostComp.rfi.answer === null ? '\r' : ''}`;
        const mkMarkup = MKMarkup.createFreeText();

        mkMarkup.setWidth(3);
        mkMarkup.setFillOpacity(0);
        mkMarkup.setStrokeColor(1, 0, 0);
        mkMarkup.setRectDiff(15, 15, 15, 15);

        mkMarkup.setBorderEffectIntensity(2);
        mkMarkup.setBorderEffectStyle(MKBorderStyle.Cloudy);

        mkMarkup.setSubject(rfiMarkupText);

        mkMarkup.setRect(box.left, box.bottom, box.right, box.top);

        const content = new MKSpan();
        content.setText(rfiMarkupText);
        content.setTextSize(18);
        content.setFontWeight(700);
        content.setAlignment('center');
        content.setVerticalAlignment('middle');
        mkMarkup.setRichContents(content);

        const richDefaults = new MKSpan();
        richDefaults.setAlignment('center');
        richDefaults.setVerticalAlignment('middle');
        mkMarkup.setRichDefaults(richDefaults);

        const creationDate = new Date().toISOString();
        mkMarkup.setCreationDate(creationDate);
        mkMarkup.setModDate(creationDate);

        const openText = {
          alignment: 'center',
          color: ['RGB', 1, 0, 0],
          fontFamily: ['Helvetica'],
          fontStretch: 'normal',
          fontStyle: 'normal',
          fontWeight: 700,
          lineHeight: 13.8,
          margin: 0,
          marginSet: false,
          strikeThrough: false,
          subscript: false,
          superscript: false,
          text: '*Open',
          textSize: 18,
          underline: false,
          verticalAlignment: 'middle',
        };

        const markupObject = mkMarkup.getAsObject();
        if (rfiPostComp.rfi.answer === null) {
          markupObject.richContents = [...markupObject.richContents, openText];
        }
        const stringifiedMarkup = JSON.stringify(markupObject);

        const markup = new MarkupRecord({
          markupid: uuidv4(),
          fileid: rfiPostComp.pageid,
          projectid: rfiPostComp.rfi.projectid,
          jsonBlob: stringifiedMarkup,
        });

        const markupComp = new Markup();
        markupComp.isRFI = true;
        markupComp.isOpen = rfiPostComp.rfi.answer === null;
        markupComp.markup = markup;

        if (entity.has(Events)) {
          const events = entity.get<Events<MapSheetEvents>>(Events);

          events.events.emit('postRFI:create', {
            markup: markupComp,
          });
        }
      }

      world.grabAll(MapboxTool).forEach(({ component: tool }) => {
        tool.tool = MapboxTools.Pointer;
      });

      entity.remove(DrawTempRectInteraction).remove(Canvas).remove(Transform);
    });
  }
};

const postRFIEvents: MapboxToolEvents = {
  mousedown: (args: MapboxEventArgs<mapboxgl.MapEventType['mousedown']>) => {
    startEvent(args);
  },

  mousemove: (args: MapboxEventArgs<mapboxgl.MapEventType['mousemove']>) => {
    moveEvent(args);
  },

  mouseup: (args: MapboxEventArgs<mapboxgl.MapEventType['mouseup']>) => {
    endEvent(args);
  },

  touchstart: (args: MapboxEventArgs<mapboxgl.MapEventType['touchstart']>) => {
    startEvent(args);
  },

  touchmove: (args: MapboxEventArgs<mapboxgl.MapEventType['touchmove']>) => {
    moveEvent(args);
  },

  touchend: (args: MapboxEventArgs<mapboxgl.MapEventType['touchend']>) => {
    endEvent(args);
  },
};

export default postRFIEvents;
